// FILE: src/services/certificationService.js
//
// Phase E1 — certification outcome recording + retrieval.
//
// Per the hybrid RLS model (§6.11.2): outcomes are recorded via the
// record_certification_outcome SECURITY DEFINER RPC, which also fires
// the certification_earned achievement atomically when outcome='passed'.
// SELECTs are RLS-filtered to the calling learner.

import { supabase } from './supabaseClient';

export const OUTCOME = {
  SCHEDULED: 'scheduled',
  PASSED:    'passed',
  FAILED:    'failed',
  WITHDREW:  'withdrew',
};

/**
 * Record a certification outcome. Atomic with the certification_earned
 * achievement firing on outcome='passed'.
 *
 * @param {object} params
 * @param {string} params.examId          — 'pmp' | 'cpmai' | 'pmi-acp'
 * @param {string} params.outcome         — scheduled | passed | failed | withdrew
 * @param {string} [params.scheduledDate] — ISO date, required when outcome='scheduled'
 * @param {string} [params.actualDate]    — ISO date, required when outcome ∈ {passed, failed, withdrew}
 * @param {string} [params.scoreBand]     — PMI A/T/B/N or learner-reported
 * @param {string} [params.notes]
 * @returns {Promise<{ id: string|null, error?: string }>}
 */
export async function recordCertificationOutcome({
  examId,
  outcome,
  scheduledDate = null,
  actualDate = null,
  scoreBand = null,
  notes = null,
}) {
  if (!examId || !outcome) return { id: null, error: 'examId and outcome are required' };

  try {
    const { data, error } = await supabase.rpc('record_certification_outcome', {
      p_exam_id:        examId,
      p_outcome:        outcome,
      p_scheduled_date: scheduledDate,
      p_actual_date:    actualDate,
      p_score_band:     scoreBand,
      p_notes:          notes,
    });
    if (error) {
      console.warn('[certificationService] RPC failed:', error.message);
      return { id: null, error: error.message };
    }
    console.info('[certificationService] outcome recorded:', examId, outcome, data);
    // For outcome='passed', the RPC already fired certification_earned in
    // the same transaction. We dispatch a learner-side event so UI can
    // celebrate (toast / overlay) without re-querying.
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      try {
        window.dispatchEvent(new CustomEvent('pm-pixel:certification-recorded', {
          detail: { examId, outcome, id: data, actualDate, scoreBand },
        }));
      } catch (_) { /* non-fatal */ }
    }
    return { id: data || null };
  } catch (err) {
    console.warn('[certificationService] unexpected error:', err?.message || err);
    return { id: null, error: err?.message || String(err) };
  }
}

/**
 * Load all recorded outcomes for the calling learner, newest first.
 * RLS filters to own rows automatically.
 */
export async function loadCertificationOutcomes() {
  try {
    const { data, error } = await supabase
      .from('learner_certifications')
      .select('*')
      .order('recorded_at', { ascending: false });
    if (error) {
      console.warn('[certificationService] load failed:', error.message);
      return [];
    }
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn('[certificationService] unexpected error reading outcomes:', err?.message || err);
    return [];
  }
}

/**
 * Helper for the UI: returns the most recent outcome row (if any) per cert.
 * Useful to decide whether to surface "Record exam outcome" or "View result".
 */
export function indexLatestByCert(rows) {
  const out = {};
  for (const r of rows || []) {
    if (!r?.exam_id) continue;
    if (!out[r.exam_id]) out[r.exam_id] = r;
  }
  return out;
}
